import React, { useState, useEffect } from 'react';
import './AdminConsole.css';

interface AdminSession {
    token: string;
    admin_id: string;
    username: string;
    permissions: string[];
    expires_in: number;
}

interface User {
    id: string;
    username: string;
    user_type: string;
    permissions: string[];
    created_at: string;
    last_login?: string;
    is_active: boolean;
}

interface Token {
    id: string;
    company_name: string;
    contact_email: string;
    permissions: string[];
    created_at: string;
    expires_at: string;
    is_active: boolean;
}

interface AdminConsoleProps {
    onClose: () => void;
}

const AdminConsole: React.FC<AdminConsoleProps> = ({ onClose }) => {
    // Authentication state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [adminSession, setAdminSession] = useState<AdminSession | null>(null);
    const [loginForm, setLoginForm] = useState({ username: '', password: '' });
    const [loginError, setLoginError] = useState('');

    // Data state
    const [users, setUsers] = useState<User[]>([]);
    const [tokens, setTokens] = useState<Token[]>([]);
    const [activeTab, setActiveTab] = useState<'users' | 'tokens' | 'create-user' | 'create-token'>('users');

    // Form state
    const [createUserForm, setCreateUserForm] = useState({
        username: '',
        password: '',
        user_type: 'standard',
        permissions: ['basic']
    });
    const [createTokenForm, setCreateTokenForm] = useState({
        company_name: '',
        contact_email: '',
        validity_days: 30,
        permissions: ['basic']
    });

    // Loading and messages
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [generatedToken, setGeneratedToken] = useState('');

    const API_BASE = process.env.REACT_APP_USER_MGMT_URL || 'http://localhost:8012';
    //const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8012';

    // Auto-logout timer
    useEffect(() => {
        if (adminSession) {
            const timer = setTimeout(() => {
                handleLogout();
                setMessage('Session expired. Please login again.');
            }, adminSession.expires_in * 1000);

            return () => clearTimeout(timer);
        }
    }, [adminSession]);

    // Load users and tokens when logged in
    useEffect(() => {
        if (isLoggedIn) {
            loadUsersAndTokens();
        }
    }, [isLoggedIn]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setLoginError('');

        //console.log("Submitting to /admin/login:", JSON.stringify(loginForm)); 

        try {
            const response = await fetch(`${API_BASE}/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginForm)
            });

            const data = await response.json();

            if (data.success) {
                setAdminSession(data.data);
                setIsLoggedIn(true);
                setMessage(`Welcome back, ${data.data.username}!`);
            } else {
                setLoginError(data.message || 'Login failed');
            }
        } catch (error) {
            setLoginError('Connection failed. Is the user management service running?');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        if (adminSession) {
            try {
                await fetch(`${API_BASE}/admin/logout`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${adminSession.token}` }
                });
            } catch (error) {
                //console.log('Logout request failed:', error);
            }
        }

        setIsLoggedIn(false);
        setAdminSession(null);
        setLoginForm({ username: '', password: '' });
        setMessage('');

        // Close the modal popup when logging out
        onClose();
    };

    const loadUsersAndTokens = async () => {
        if (!adminSession) return;

        try {
            const response = await fetch(`${API_BASE}/admin/users`, {
                headers: { 'Authorization': `Bearer ${adminSession.token}` }
            });

            const data = await response.json();
            if (data.success) {
                //console.log("All users data:", data.data.users);
                //data.data.users.forEach((user, index) => {
                //    console.log(`User ${index} permissions:`, user.permissions, typeof user.permissions);
                //});

                // Parse permissions strings back to arrays
                const usersWithParsedPermissions = data.data.users.map(user => ({
                    ...user,
                    permissions: typeof user.permissions === 'string'
                        ? JSON.parse(user.permissions)
                        : user.permissions
                }));

                const tokensWithParsedPermissions = data.data.tokens.map(token => ({
                    ...token,
                    permissions: typeof token.permissions === 'string'
                        ? JSON.parse(token.permissions)
                        : token.permissions
                }));

                setUsers(usersWithParsedPermissions);
                setTokens(tokensWithParsedPermissions);
            }
        } catch (error) {
            setMessage('Failed to load users and tokens');
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!adminSession) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/admin/create-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminSession.token}`
                },
                body: JSON.stringify(createUserForm)
            });

            const data = await response.json();
            if (data.success) {
                setMessage(`User "${createUserForm.username}" created successfully!`);
                setCreateUserForm({ username: '', password: '', user_type: 'standard', permissions: ['basic'] });
                loadUsersAndTokens();
                setActiveTab('users');
            } else {
                setMessage(`Error: ${data.message}`);
            }
        } catch (error) {
            setMessage('Failed to create user');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateToken = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!adminSession) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/admin/create-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminSession.token}`
                },
                body: JSON.stringify(createTokenForm)
            });

            const data = await response.json();
            if (data.success) {
                setGeneratedToken(data.data.token);
                setMessage(`Token created for ${createTokenForm.company_name}!`);
                setCreateTokenForm({ company_name: '', contact_email: '', validity_days: 30, permissions: ['basic'] });
                loadUsersAndTokens();
            } else {
                setMessage(`Error: ${data.message}`);
            }
        } catch (error) {
            setMessage('Failed to create token');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setMessage('Token copied to clipboard!');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    if (!isLoggedIn) {
        return (
            <div className="admin-console-overlay">
                <div className="admin-console-modal">
                    <div className="admin-console-header">
                        <h2> Admin Console</h2>
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>

                    <form onSubmit={handleLogin} className="admin-login-form">
                        <div className="form-group">
                            <label>Admin Username:</label>
                            <input
                                type="text"
                                value={loginForm.username}
                                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                                required
                                placeholder="Enter admin username"
                            />
                        </div>

                        <div className="form-group">
                            <label>Password:</label>
                            <input
                                type="password"
                                value={loginForm.password}
                                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                required
                                placeholder="Enter admin password"
                            />
                        </div>

                        {loginError && <div className="error-message">{loginError}</div>}

                        <button type="submit" disabled={loading} className="login-button">
                            {loading ? 'Logging in...' : 'Admin Login'}
                        </button>
                    </form>
                    {/*
                    <div className="admin-info">
                        <p><strong>Default Admin:</strong></p>
                        <p>Username: admin</p>
                        <p>Password: admin123</p>
                        <p><em>Change this in production!</em></p>
                    </div>
                        */}

                </div>
            </div>
        );
    }

    return (
        <div className="admin-console-overlay">
            <div className="admin-console-modal large">
                <div className="admin-console-header">
                    <h2> Admin Console - {adminSession?.username}</h2>
                    <div className="header-actions">
                        <button onClick={handleLogout} className="logout-button">Logout</button>
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>
                </div>

                {message && <div className="status-message">{message}</div>}

                <div className="admin-tabs">
                    <button
                        className={activeTab === 'users' ? 'tab active' : 'tab'}
                        onClick={() => setActiveTab('users')}
                    >
                        Users ({users.length})
                    </button>
                    <button
                        className={activeTab === 'tokens' ? 'tab active' : 'tab'}
                        onClick={() => setActiveTab('tokens')}
                    >
                        Tokens ({tokens.length})
                    </button>
                    <button
                        className={activeTab === 'create-user' ? 'tab active' : 'tab'}
                        onClick={() => setActiveTab('create-user')}
                    >
                        Create User
                    </button>
                    <button
                        className={activeTab === 'create-token' ? 'tab active' : 'tab'}
                        onClick={() => setActiveTab('create-token')}
                    >
                        Create Token
                    </button>
                </div>

                <div className="admin-content">
                    {activeTab === 'users' && (
                        <div className="users-list">
                            <h3>Username/Password Users</h3>
                            {users.length === 0 ? (
                                <p>No users created yet.</p>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Username</th>
                                            <th>Type</th>
                                            <th>Permissions</th>
                                            <th>Created</th>
                                            <th>Last Login</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map(user => (
                                            <tr key={user.id}>
                                                <td>{user.username}</td>
                                                <td>{user.user_type}</td>
                                                <td>{user.permissions.join(', ')}</td>
                                                <td>{formatDate(user.created_at)}</td>
                                                <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                                                <td>
                                                    <span className={user.is_active ? 'status-active' : 'status-inactive'}>
                                                        {user.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === 'tokens' && (
                        <div className="tokens-list">
                            <h3>Offline Access Tokens</h3>
                            {tokens.length === 0 ? (
                                <p>No tokens created yet.</p>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Company</th>
                                            <th>Contact Email</th>
                                            <th>Permissions</th>
                                            <th>Created</th>
                                            <th>Expires</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tokens.map(token => (
                                            <tr key={token.id}>
                                                <td>{token.company_name}</td>
                                                <td>{token.contact_email}</td>
                                                <td>{token.permissions.join(', ')}</td>
                                                <td>{formatDate(token.created_at)}</td>
                                                <td>{formatDate(token.expires_at)}</td>
                                                <td>
                                                    <span className={token.is_active ? 'status-active' : 'status-inactive'}>
                                                        {token.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === 'create-user' && (
                        <div className="create-user-form">
                            <h3>Create New User Account</h3>
                            <form onSubmit={handleCreateUser}>
                                <div className="form-group">
                                    <label>Username:</label>
                                    <input
                                        type="text"
                                        value={createUserForm.username}
                                        onChange={(e) => setCreateUserForm({ ...createUserForm, username: e.target.value })}
                                        required
                                        placeholder="Enter username"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Password:</label>
                                    <input
                                        type="password"
                                        value={createUserForm.password}
                                        onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                                        required
                                        placeholder="Enter secure password"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>User Type:</label>
                                    <select
                                        value={createUserForm.user_type}
                                        onChange={(e) => setCreateUserForm({ ...createUserForm, user_type: e.target.value })}
                                    >
                                        <option value="standard">Standard User</option>
                                        <option value="admin">Admin User</option>
                                    </select>
                                </div>

                                <button type="submit" disabled={loading} className="create-button">
                                    {loading ? 'Creating...' : 'Create User'}
                                </button>
                            </form>
                        </div>
                    )}

                    {activeTab === 'create-token' && (
                        <div className="create-token-form">
                            <h3>Generate Offline Access Token</h3>
                            <form onSubmit={handleCreateToken}>
                                <div className="form-group">
                                    <label>Company Name:</label>
                                    <input
                                        type="text"
                                        value={createTokenForm.company_name}
                                        onChange={(e) => setCreateTokenForm({ ...createTokenForm, company_name: e.target.value })}
                                        required
                                        placeholder="e.g. Acme Corporation"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contact Email:</label>
                                    <input
                                        type="email"
                                        value={createTokenForm.contact_email}
                                        onChange={(e) => setCreateTokenForm({ ...createTokenForm, contact_email: e.target.value })}
                                        required
                                        placeholder="contact@company.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Validity (Days):</label>
                                    <input
                                        type="number"
                                        value={createTokenForm.validity_days}
                                        onChange={(e) => setCreateTokenForm({ ...createTokenForm, validity_days: parseInt(e.target.value) })}
                                        min="1"
                                        max="365"
                                        required
                                    />
                                </div>

                                <button type="submit" disabled={loading} className="create-button">
                                    {loading ? 'Generating...' : 'Generate Token'}
                                </button>
                            </form>

                            {generatedToken && (
                                <div className="generated-token">
                                    <h4> Token Generated Successfully!</h4>
                                    <div className="token-display">
                                        <code>{generatedToken}</code>
                                        <button onClick={() => copyToClipboard(generatedToken)} className="copy-button">
                                            Copy
                                        </button>
                                    </div>
                                    <p className="token-warning">
                                        <strong>Important:</strong> Save this token securely. It cannot be retrieved again.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminConsole;